/**
 * RevealCountdownChip — VÉRAULT Member Suite (Wave 30 · Phase 7e).
 * ─────────────────────────────────────────────────────────────────
 * Quiet, cinematic countdown chip surfaced on the HeroZone when:
 *   - The trip is currently VEILED, AND
 *   - `destination_reveal_at` is set, AND
 *   - The reveal moment is within the next 48 hours.
 *
 * Design: glass-morphism pill anchored top-center over the hero,
 * with a small Sparkles icon + "REVEAL IN" eyebrow + live countdown.
 * Auto-ticks every 30s and reformats from "in 8h" → "in 3h" → "in
 * 12 min" → "any moment". When the moment passes, the chip
 * disappears — the existing veil-lift cross-fade takes over.
 *
 * Never says the destination name (veil-safe by design).
 */
import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles } from "lucide-react";
import { formatTripLocalTime, getTripTimezone } from "../../../../lib/timezone";

const WINDOW_HOURS = 48;
const MIN_MS = 60_000;
const HOUR_MS = 3_600_000;

function formatRemaining(diffMs) {
  if (diffMs <= 0) return "any moment";
  const totalMin = Math.round(diffMs / MIN_MS);
  if (totalMin < 1) return "any moment";
  if (totalMin < 60) return `in ${totalMin} min`;
  const totalHours = Math.round(diffMs / HOUR_MS);
  if (totalHours < 24) return `in ${totalHours}h`;
  const days = Math.round(diffMs / (24 * HOUR_MS));
  return `in ${days}d`;
}

export default function RevealCountdownChip({ trip, veiled }) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const tick = window.setInterval(() => setNow(Date.now()), 30000);
    return () => window.clearInterval(tick);
  }, []);

  if (!veiled) return null;
  const at = trip?.destination_reveal_at;
  if (!at) return null;

  const target = (() => {
    try {
      return new Date(at).getTime();
    } catch {
      return null;
    }
  })();
  if (!target) return null;

  const diff = target - now;
  // Show only within the 48h window (calm — no week-out drumming).
  if (diff > WINDOW_HOURS * HOUR_MS) return null;
  // After the moment passes, the veil-lift cross-fade owns the screen.
  if (diff < -2 * MIN_MS) return null;

  const tz = getTripTimezone(trip);
  const localTime = formatTripLocalTime(at, tz, {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });

  return (
    <AnimatePresence>
      <motion.div
        key="reveal-countdown-chip"
        initial={{ opacity: 0, y: -6 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -6 }}
        transition={{ duration: 0.7, ease: [0.22, 0.61, 0.36, 1] }}
        className="absolute top-5 sm:top-7 left-1/2 -translate-x-1/2 z-10"
        data-testid="reveal-countdown-chip"
        data-reveal-window={diff <= HOUR_MS ? "imminent" : "soon"}
      >
        <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[hsl(var(--accent-gold))]/55 bg-black/35 backdrop-blur-sm font-mono text-[10px] uppercase tracking-[0.28em] text-white">
          <Sparkles className="size-3 text-[hsl(var(--accent-gold))]" strokeWidth={1.5} />
          <span>Reveal</span>
          <span className="opacity-70">·</span>
          <span data-testid="reveal-countdown-remaining">{formatRemaining(diff)}</span>
          {localTime ? (
            <>
              <span className="opacity-60">·</span>
              <span className="opacity-80">{localTime}</span>
            </>
          ) : null}
        </span>
      </motion.div>
    </AnimatePresence>
  );
}
